import { useI18n, useSlotText } from "@/i18n/translations";
import { chiSiamo } from "@/data/content";
import { presidenza, segreteriaEmail, comitatoScientifico, consiglioRicerca } from "@/data/organi";

/** Ruoli e paesi dell'organigramma: localizzati in lettura (i nomi restano verbatim). */
const RUOLI_EN: Record<string, string> = {
  Presidente: "President",
  Direttore: "Director",
  Segretario: "Secretary",
  "Responsabile Amministrativo delle Ricerche": "Research Administration Manager",
};
const PAESI_EN: Record<string, string> = {
  Portogallo: "Portugal",
  Germania: "Germany",
  Spagna: "Spain",
  Italia: "Italy",
  Usa: "USA",
  Svizzera: "Switzerland",
  Francia: "France",
};
const ruoloMostrato = (r: string, l: string) => (l === "en" ? RUOLI_EN[r] ?? r : r);
const affiliazioneMostrata = (a: string | undefined, l: string) =>
  a ? (l === "en" ? a.replace(/—\s*(Portogallo|Germania|Spagna|Italia|Usa|Svizzera|Francia)\s*$/, (mm, p) => `— ${PAESI_EN[p]}`) : a) : a;

import { Seo } from "@/components/Seo";
import { DemoBadge, FotoSlot, NoteCard, PageHeader, SlotText } from "@/components/Editorial";
import { Reveal } from "@/components/Reveal";
import { Download } from "lucide-react";

export const ChiSiamo = () => {
  const { t, lang } = useI18n();
  const slotText = useSlotText();

  return (
    <>
      <Seo titleKey="seo.chi.title" descKey="seo.chi.desc" path="/chi-siamo" />
      <PageHeader
        kickerKey="chi.kicker"
        titleKey="chi.title"
        aside={
          <FotoSlot
            src="https://thumb.wikimedia.org/wikipedia/commons/thumb/5/56/Genova_Universit%C3%A0_di_Genova_Cortile_7.jpg/960px-Genova_Universit%C3%A0_di_Genova_Cortile_7.jpg"
            captionKey="foto.storia"
            testid="foto-storia"
            ratio="aspect-[21/9] sm:aspect-auto sm:min-h-[140px]"
            fill
          />
        }
      >
        <p className="mt-6 max-w-2xl text-base leading-relaxed text-mutedtx">
          <SlotText slot={chiSiamo.intro} />
        </p>
        <DemoBadge className="mt-4" />
      </PageHeader>

      {/* STORIA — bozza di riscrittura (voce Bompiani come fonte, mai ripubblicata) + testo Sola in arrivo */}
      <section className="container-x py-16 sm:py-20" data-testid="chi-siamo-storia">
        <Reveal>
          <h2 className="font-serif text-3xl font-semibold tracking-tight text-ink sm:text-4xl">{t("chi.storia_title")}</h2>
          <p className="mt-3"><DemoBadge label={t("storia.bozza")} /></p>
        </Reveal>
        <div className="mt-8 space-y-6">
          <Reveal delay={0.05}>
            <p className="leading-relaxed text-bodytx/90"><SlotText slot={chiSiamo.storia1} /></p>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="leading-relaxed text-bodytx/90"><SlotText slot={chiSiamo.storia2} /></p>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="leading-relaxed text-bodytx/90"><SlotText slot={chiSiamo.storia3} /></p>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="leading-relaxed text-bodytx/90">
              <SlotText slot={chiSiamo.solaSlot} />
            </p>
            <DemoBadge className="mt-3" />
          </Reveal>
        </div>
        <Reveal className="mt-10">
          <NoteCard title={t("att.nota_title")}>{t("chi.storia_note")}</NoteCard>
        </Reveal>
        <Reveal className="mt-4">
          <NoteCard title={t("common.aggiornamento")}>
            <SlotText slot={chiSiamo.fondazioneNota} />
          </NoteCard>
        </Reveal>
      </section>

      {/* STRUTTURA — organigramma ufficiale 2026 */}
      <section className="border-y border-borderc bg-cardwhite py-16 sm:py-20" data-testid="organigram-2026-container">
        <div className="container-x">
          <Reveal>
            <h2 className="font-serif text-3xl font-semibold tracking-tight text-ink sm:text-4xl">{t("chi.struttura_title")}</h2>
            <p className="mt-4 text-sm leading-relaxed text-mutedtx">{t("chi.privacy_nota")}</p>
          </Reveal>
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {presidenza.map((c, i) => (
              <Reveal key={c.ruolo} delay={i * 0.06}>
                <article className="flex h-full flex-col border border-borderc bg-paper p-7" data-testid={`carica-${i}`}>
                  <span className="eyebrow text-bordeaux">{ruoloMostrato(c.ruolo, lang)}</span>
                  <p className="mt-3 font-serif text-2xl font-medium text-ink">{c.nome}</p>
                </article>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-6">
            <p className="text-sm text-mutedtx">
              {t("con.segretaria_title")}: <a href={`mailto:${segreteriaEmail}`} className="link-underline font-semibold text-ink" data-testid="segreteria-email-organigramma">{segreteriaEmail}</a>
            </p>
          </Reveal>
          <Reveal className="mt-8 flex flex-wrap gap-x-10 gap-y-4">
            <a href="/materiali/elenco-email-2026.docx" target="_blank" rel="noopener noreferrer" data-testid="download-elenco-email" className="link-underline inline-flex items-center gap-2 text-[13px] font-semibold uppercase tracking-[0.12em] text-bordeaux">
              <Download className="h-4 w-4" aria-hidden="true" />
              {t("chi.elenco_email_docx")}
            </a>
            <a href="/materiali/statuto-cesp.pdf" target="_blank" rel="noopener noreferrer" data-testid="download-statuto" className="link-underline inline-flex items-center gap-2 text-[13px] font-semibold uppercase tracking-[0.12em] text-bordeaux">
              <Download className="h-4 w-4" aria-hidden="true" />
              {t("chi.statuto_pdf")}
            </a>
          </Reveal>
          <Reveal className="mt-4">
            <div className="border border-amber-500/40 bg-amber-50 px-3.5 py-2.5 text-[12px] leading-relaxed text-amber-900" data-testid="struttura-nota-cellulari">
              <span className="mr-2 inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.14em]">
                <span className="h-1.5 w-1.5 rounded-full bg-amber-500" aria-hidden="true" />
                {t("common.nota_da_confermare")}
              </span>
              {t("chi.nota_cellulari")}
            </div>
          </Reveal>
        </div>
      </section>

      {/* COMITATO SCIENTIFICO INTERNAZIONALE */}
      <section className="container-x py-16 sm:py-20" data-testid="chi-siamo-comitato">
        <Reveal>
          <h2 className="font-serif text-3xl font-semibold tracking-tight text-ink sm:text-4xl">{t("chi.comitato_title")}</h2>
        </Reveal>
        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="comitato-list">
          {comitatoScientifico.map((m, i) => (
            <Reveal key={m.nome} delay={Math.min(i * 0.04, 0.3)} y={14}>
              <div className="h-full border border-borderc bg-cardwhite p-5" data-testid={`comitato-membro-${i}`}>
                <p className="font-serif text-lg font-medium text-ink">{m.nome}</p>
                <p className="mt-1 text-xs leading-relaxed text-mutedtx">{affiliazioneMostrata(m.affiliazione, lang)}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CONSIGLIO DI RICERCA */}
      <section className="container-x pb-20 sm:pb-24" data-testid="chi-siamo-consiglio">
        <Reveal>
          <h2 className="font-serif text-3xl font-semibold tracking-tight text-ink sm:text-4xl">{t("chi.consiglio_title")}</h2>
          <p className="mt-3 text-xs uppercase tracking-[0.14em] text-mutedtx">{t("chi.consiglio_aggiornato")}</p>
        </Reveal>
        <ol className="mt-8 grid gap-x-16 border-t border-borderc md:grid-cols-2" data-testid="consiglio-list">
          {consiglioRicerca.map((nome, i) => (
            <li key={nome} className="flex items-baseline gap-4 border-b border-borderc/70 py-3" data-testid={`consiglio-membro-${i}`}>
              <span className="font-serif text-sm italic text-bordeaux">{String(i + 1).padStart(2, "0")}</span>
              <span className="text-sm font-medium text-ink">{nome}</span>
            </li>
          ))}
        </ol>
        <Reveal className="mt-6">
          <p className="max-w-2xl text-xs italic leading-relaxed text-mutedtx">{t("chi.privacy_nota")}</p>
        </Reveal>
      </section>
    </>
  );
};

export default ChiSiamo;
